import {
  InlineEditorLogic,
  isEditMode,
  TagsEditor,
  TagsEditorState,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { TextLinkWidget } from '@tableau/widgets';
import { isEqual } from 'lodash';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';
import { ITag } from '~src/tags/ITag';
import { TagsHelper } from '~src/tags/TagsInputHelper';

import { ACTION_DIALOG_ID } from '../ContentDetailsDialog';
import { ContentDetailSection, DetailEditButton, DetailSaveCancelButtons } from '../ContentDetailSection';

// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID+'-Tags';

export interface TagsDetailSectionProps {
  readonly tags?: Map<string,number>;
  readonly isActionEnabled: boolean;
  readonly setTags: (tags: string []) => Promise<IResult>;
  readonly tagUrlFn?: (tag: string) => string;
  readonly incrementRouterSpaceReactKey: () => void;
}

export function TagsDetailSection(props: TagsDetailSectionProps): JSX.Element {

  const handleFilterItems = (partialText: string) => {
    return (
      SearchSuggestionsService.tags(partialText)
        .getSlice(0, 10)
        .then((result: ListResult<ITag>) => {
          return result.items.map((value: ITag) => value.name);
        })
    );
  };

  const onSave = React.useCallback(
    async (editorState: TagsEditorState) => {
      await props.setTags(TagsHelper.getTagNamesFromMap(editorState.tags))
        .then(rejectIfResultContainsError)
        .then(props.incrementRouterSpaceReactKey);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.setTags,
      props.incrementRouterSpaceReactKey
    ]
  );

  const canSave = (editorState: TagsEditorState) => {
    return !isEqual(editorState.tags, initialEditorState.tags);
  };

  const initialEditorState: TagsEditorState = {
    tags: props.tags,
    suggestions: [],
    partialText: '',
  };

  const logic: InlineEditorLogic<TagsEditorState> = useInlineEditor<TagsEditorState>({
    editorStateWhenEnteringEditMode: initialEditorState,
    canEdit: props.isActionEnabled,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header = {Messages.column_tags()}
        testId = {'tags'}
        headerId={sectionHeaderId}
        detail = {
          <TagsEditor
            aria-labelledby={sectionHeaderId}
            editorState={logic.editorState}
            setEditorState={logic.setEditorStateWithinEditMode}
            searchMatchingTags={handleFilterItems}
            disableEditor={logic.isActionBusy}
            itemCount={1}
          />
        }
        isActionAllowed={true}
        action = {
          <DetailSaveCancelButtons
            inlineEditorLogic={logic}
          />
        }
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header = {Messages.column_tags()}
        testId = {'tags'}
        detail = {
          <TagsReadingMode
            displayedTags = {TagsHelper.getTagNamesFromMap(props.tags)}
            tagUrlFn = {props.tagUrlFn}
          />
        }
        isActionAllowed={logic.canEdit}
        action = {
          <DetailEditButton
            buttonRef={logic.editButtonRef}
            buttonLabel={Messages.editTags()}
            buttonTestId={'edit-tags'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}

export interface TagsReadingModeProps {
  readonly displayedTags: string [];
  readonly tagUrlFn?: (tag: string) => string;
}

export function TagsReadingMode(props: TagsReadingModeProps): JSX.Element {
  const [tagLinks, setTagLinks] = React.useState<any[]>([]);

  React.useEffect(
    () => {
      var processedLinks: any[] = [];

      for (let index = 0; index < props.displayedTags?.length; index++) {
        const tagName = props.displayedTags[index];
        const tagLink = props.tagUrlFn ? props.tagUrlFn(tagName) : '';

        if(index > 0) {
          processedLinks.push(<span key={`comma${index}`}>, </span>);
        }

        if(tagLink === '') {
          processedLinks.push(<span key={tagName}>{tagName}</span>);
        } else {
          processedLinks.push(
            <TextLinkWidget
              testId="tag"
              href={tagLink}
              children={tagName}
              key={tagName}
            />);
        }
      }

      setTagLinks(processedLinks);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if(tagLinks.length === 0) {
    return null;
  } else {
    return (
      <div>{ tagLinks }</div>
    );
  }
}
